// ============================================================================
// LES OBJECTIFS DE VENTE — la règle qui répartit l'objectif d'un mois sur ses jours.
//
// UTILITÉ : la saisie, le suivi jour après jour et le Résumé du back-office — et demain l'objectif du jour en caisse —
// répartissent le même mois de la même façon. Écrite deux fois, la cible d'un samedi différerait d'un écran à l'autre.
//
// ⚠️ LE MOIS EST LA DÉCISION, LE JOUR EN EST UNE PART : Σ des jours = l'objectif du mois, au centime (plus fort reste,
//    `allocate`). Une fermeture ou un événement déplace la cible d'un jour sur les autres jours du mois, jamais ne
//    change celle du mois.
// ⚠️ UN JOUR PEUT ÊTRE FIXÉ : il garde le montant saisi, et c'est le RESTE du mois qui se répartit sur les autres jours
//    selon leur poids. Quand TOUS les jours qui portent une cible sont fixés, l'objectif du mois est leur somme — la
//    saisie au jour le jour n'est donc pas un second système, c'est le même poussé jusqu'au bout.
// ⚠️ LE POIDS D'UN JOUR est celui de son jour de semaine (100 = un jour moyen), multiplié par la variation d'un
//    événement ; il est NUL un jour fermé, que la fermeture soit habituelle ou exceptionnelle.
// ⚠️ LES JOURS DE SEMAINE SONT NUMÉROTÉS 1 = lundi … 7 = dimanche, comme le calendrier du magasin.
// ============================================================================
import { allocate, mulDivRound } from '@orgazone/core-money';

/** Le poids le plus fort qu'un jour de semaine peut recevoir (quatre jours moyens). */
export const OBJECTIVE_WEIGHT_MAX = 400;
/** Le poids d'un jour moyen — celui de tous les jours quand rien d'autre ne le dit. */
export const OBJECTIVE_WEIGHT_NEUTRAL = 100;
/** La baisse la plus forte d'un événement : −99 %. À −100 %, c'est une fermeture, qui se déclare comme telle. */
export const OBJECTIVE_CHANGE_MIN_BP = -9_900;
/** La hausse la plus forte d'un événement : +300 %. */
export const OBJECTIVE_CHANGE_MAX_BP = 30_000;
/** Le plus gros objectif mensuel accepté, en unités mineures — au-delà, c'est une faute de frappe. */
export const OBJECTIVE_AMOUNT_MAX = 99_999_999_999;

/**
 * Ce qu'un jour a de particulier — une seule chose à la fois : une fermeture, un événement qui change sa cible de
 * `changeBp`, ou un montant FIXÉ à la main, qui remplace sa part du mois.
 */
export interface ObjectiveException {
  kind: 'closed' | 'event' | 'fixed';
  /** Variation de l'événement en points de base (+30 % = 3000) ; `null` sinon. */
  changeBp: number | null;
  /** L'objectif saisi d'un jour fixé, en unités mineures ; `null` sinon. */
  amountCents: number | null;
}

/** Ce qu'il faut savoir du magasin pour répartir un mois. */
export interface ObjectiveCalendar {
  /** Les jours d'ouverture, 1 = lundi … 7 = dimanche. */
  openDays: readonly number[];
  /** Le poids de chaque jour de semaine — index 0 = lundi … 6 = dimanche. */
  weights: readonly number[];
  /** Les jours exceptionnels, par jour `AAAAMMJJ`. */
  exceptions: ReadonlyMap<string, ObjectiveException>;
}

/** Une date `AAAAMMJJ` en millisecondes UTC (minuit). */
const utcOf = (ymd: string): number => Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(4, 6)) - 1, Number(ymd.slice(6, 8)));

/** Le jour de semaine d'une date `AAAAMMJJ`, 1 = lundi … 7 = dimanche. */
export function weekdayOf(ymd: string): number {
  return ((new Date(utcOf(ymd)).getUTCDay() + 6) % 7) + 1;
}

const MONTH = /^\d{4}(0[1-9]|1[0-2])$/;

/** `AAAAMM` désigne-t-il un mois ? */
export const isObjectiveMonth = (v: unknown): v is string => typeof v === 'string' && MONTH.test(v);

/** `AAAAMMJJ` désigne-t-il un jour qui existe (pas de 31 avril) ? */
export function isObjectiveDay(v: unknown): v is string {
  if (typeof v !== 'string' || !/^\d{8}$/.test(v) || !MONTH.test(v.slice(0, 6))) return false;
  const d = new Date(utcOf(v));
  return d.getUTCDate() === Number(v.slice(6, 8));
}

/** Les jours d'un mois `AAAAMM`, dans l'ordre. */
export function monthDays(month: string): string[] {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(4, 6));
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return Array.from({ length: last }, (_, i) => `${month}${String(i + 1).padStart(2, '0')}`);
}

/**
 * Le poids d'un jour, ENTIER : poids du jour de semaine × (10 000 + variation de l'événement). Nul un jour fermé.
 * ⚠️ ENTIER ET À L'ÉCHELLE 10 000 : la répartition se fait au plus fort reste sur des entiers, et un +30 % ne doit pas
 *    passer par un flottant pour en arriver là.
 * ⚠️ UN JOUR FIXÉ N'EN A PAS : il ne prend aucune part du reste du mois, il a la sienne.
 */
export function dayWeight(day: string, cal: ObjectiveCalendar): number {
  const wd = weekdayOf(day);
  if (!cal.openDays.includes(wd)) return 0;
  const ex = cal.exceptions.get(day);
  if (ex?.kind === 'closed' || ex?.kind === 'fixed') return 0;
  const w = Math.max(0, Math.trunc(cal.weights[wd - 1] ?? 0));
  return w * (10_000 + (ex?.kind === 'event' ? ex.changeBp ?? 0 : 0));
}

/**
 * L'objectif d'un jour : son montant fixé, ou sa part du reste du mois ; `null` un jour qui n'en porte pas (fermé, de
 * poids nul, ou d'un mois sans objectif).
 */
export interface DayObjective { day: string; objectiveCents: number | null; fixed: boolean }

/**
 * CE QUE DONNE UN MOIS, réparti sur ses jours :
 * - `split` : l'objectif du mois, les jours fixés gardant leur montant et le reste allant aux autres selon leur poids ;
 * - `days` : TOUS les jours qui portent une cible sont fixés — l'objectif du mois est leur somme (`monthCents`) ;
 * - `partial` : pas d'objectif mensuel, mais des jours fixés — eux seuls portent une cible ;
 * - `over` : les jours fixés dépassent l'objectif du mois de `overCents` — les autres jours n'ont AUCUNE cible : une part
 *   négative n'a pas de sens, et l'écran doit le dire ;
 * - `nowhere` : un objectif mensuel, et aucun jour où le poser (tout fermé, ou tous les poids à zéro) — jamais de parts
 *   égales inventées ;
 * - `none` : ni objectif mensuel, ni jour fixé.
 */
export type MonthDistribution =
  | { state: 'split' | 'days'; monthCents: number; days: DayObjective[] }
  | { state: 'partial'; days: DayObjective[] }
  | { state: 'over'; monthCents: number; overCents: number; days: DayObjective[] }
  | { state: 'nowhere'; monthCents: number }
  | { state: 'none' };

/** Répartit l'objectif d'un mois (`null` : il n'en a pas) sur ses jours — voir `MonthDistribution`. */
export function distributeMonth(month: string, amountCents: number | null, cal: ObjectiveCalendar): MonthDistribution {
  const days = monthDays(month);
  const fixed = new Map<string, number>();
  for (const d of days) {
    const ex = cal.exceptions.get(d);
    if (ex?.kind === 'fixed' && ex.amountCents !== null) fixed.set(d, ex.amountCents);
  }
  const weights = days.map((d) => dayWeight(d, cal));
  const free = weights.some((w) => w > 0);
  const fixedCents = [...fixed.values()].reduce((s, x) => s + x, 0);
  // Les jours non fixés reçoivent leur part de `rest`, ou rien (`null`).
  const shaped = (rest: number | null): DayObjective[] => {
    const carrying = days.filter((_, i) => weights[i]! > 0);
    const parts = rest === null ? [] : allocate(rest, weights.filter((w) => w > 0));
    const shares = new Map(carrying.map((d, i) => [d, parts[i] ?? null]));
    return days.map((day) => (fixed.has(day)
      ? { day, objectiveCents: fixed.get(day)!, fixed: true }
      : { day, objectiveCents: shares.get(day) ?? null, fixed: false }));
  };
  if (fixed.size && !free) return { state: 'days', monthCents: fixedCents, days: shaped(null) };
  if (amountCents === null) return fixed.size ? { state: 'partial', days: shaped(null) } : { state: 'none' };
  if (!free) return { state: 'nowhere', monthCents: amountCents };
  if (fixedCents > amountCents) return { state: 'over', monthCents: amountCents, overCents: fixedCents - amountCents, days: shaped(null) };
  return { state: 'split', monthCents: amountCents, days: shaped(amountCents - fixedCents) };
}

/**
 * L'objectif d'un mois tel qu'il s'applique : celui saisi, ou la somme de ses jours quand ils sont tous fixés.
 * ⚠️ UN MOIS QUI NE SE RÉPARTIT PAS GARDE SON OBJECTIF : il est saisi, seule sa répartition manque.
 */
export function monthObjectiveOf(d: MonthDistribution): number | null {
  return d.state === 'none' || d.state === 'partial' ? null : d.monthCents;
}

/**
 * LE PROFIL DES JOURS d'après l'historique — le poids de chaque jour de semaine, 100 = la moyenne des jours ouverts.
 * `htByWeekday` porte le chiffre HT de chaque jour de semaine (index 0 = lundi) sur des semaines ENTIÈRES, pour que
 * chaque jour y figure le même nombre de fois.
 * `null` quand l'historique ne dit rien (aucun jour ouvert n'a vendu) : l'écran le dit, et tous les jours valent 100.
 * ⚠️ BORNÉ À `OBJECTIVE_WEIGHT_MAX` : un profil est un point de départ que la saisie reprend tel quel, il doit donc
 *    rester une saisie acceptable.
 * ⚠️ UN JOUR FERMÉ VAUT ZÉRO, même s'il a vendu un jour d'ouverture exceptionnelle : il ne reçoit pas d'objectif.
 */
export function weekdayProfile(htByWeekday: readonly number[], openDays: readonly number[]): number[] | null {
  const totals = [1, 2, 3, 4, 5, 6, 7].map((wd) => (openDays.includes(wd) ? Math.max(0, Math.trunc(htByWeekday[wd - 1] ?? 0)) : 0));
  const selling = totals.filter((x) => x > 0);
  if (!selling.length) return null;
  const sum = selling.reduce((s, x) => s + x, 0);
  return totals.map((x) => Math.min(OBJECTIVE_WEIGHT_MAX, mulDivRound(x, 100 * selling.length, sum)));
}

/** Pourquoi un objectif mensuel est refusé — un code que l'écran traduit. */
export type ObjectiveAmountRefusal = 'amount_not_integer' | 'amount_not_positive' | 'amount_too_large';

/** Un objectif mensuel est un montant entier positif ; `null` (le retirer) se dit autrement. */
export function objectiveAmountRefusal(cents: unknown): ObjectiveAmountRefusal | null {
  if (typeof cents !== 'number' || !Number.isInteger(cents)) return 'amount_not_integer';
  if (cents <= 0) return 'amount_not_positive';
  if (cents > OBJECTIVE_AMOUNT_MAX) return 'amount_too_large';
  return null;
}

/** Pourquoi des poids sont refusés. */
export type ObjectiveWeightsRefusal = 'weights_count' | 'weight_out_of_range';

/**
 * Les poids se saisissent ENSEMBLE — les sept jours, ou aucun (le profil de l'historique).
 * ⚠️ JAMAIS À MOITIÉ : trois poids saisis et quatre pris du profil feraient une semaine que personne n'a décidée.
 */
export function objectiveWeightsRefusal(values: unknown): ObjectiveWeightsRefusal | null {
  if (!Array.isArray(values) || values.length !== 7) return 'weights_count';
  if (!values.every((v) => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= OBJECTIVE_WEIGHT_MAX)) return 'weight_out_of_range';
  return null;
}

/** Pourquoi un jour particulier est refusé. */
export type ObjectiveExceptionRefusal = 'day_invalid' | 'kind_unknown' | 'change_required' | 'change_out_of_range' | 'change_zero'
  | 'change_on_closure' | 'change_on_fixed' | 'amount_on_exception' | ObjectiveAmountRefusal;

/**
 * Un jour particulier : un jour qui existe, et une fermeture (sans variation), un événement (variation non nulle, entre
 * −99 % et +300 %) ou un jour fixé (un montant, la règle d'un objectif mensuel).
 * ⚠️ UN ÉVÉNEMENT À 0 % EST REFUSÉ : il ne change rien, et l'afficher ferait croire qu'il a été pris en compte.
 * ⚠️ UN JOUR FIXÉ À ZÉRO EST REFUSÉ : un jour sans cible est une fermeture, qui se déclare comme telle.
 */
export function objectiveExceptionRefusal(e: { day: unknown; kind: unknown; changeBp?: unknown; amountCents?: unknown }): ObjectiveExceptionRefusal | null {
  if (!isObjectiveDay(e.day)) return 'day_invalid';
  if (e.kind === 'fixed') return e.changeBp != null ? 'change_on_fixed' : objectiveAmountRefusal(e.amountCents);
  if (e.kind !== 'closed' && e.kind !== 'event') return 'kind_unknown';
  if (e.amountCents != null) return 'amount_on_exception';
  if (e.kind === 'closed') return e.changeBp == null ? null : 'change_on_closure';
  if (e.changeBp == null) return 'change_required';
  if (typeof e.changeBp !== 'number' || !Number.isInteger(e.changeBp)
    || e.changeBp < OBJECTIVE_CHANGE_MIN_BP || e.changeBp > OBJECTIVE_CHANGE_MAX_BP) return 'change_out_of_range';
  if (e.changeBp === 0) return 'change_zero';
  return null;
}

/**
 * LIT UNE VARIATION SAISIE EN POUR CENT (« 5 », « -12,5 », « +30 % ») — en points de base entiers, `null` si elle n'en est
 * pas une. Une progression d'exercice ou un événement se SIGNE : une baisse est une saisie légitime.
 * ⚠️ REFUSER, JAMAIS CORRIGER : trois décimales ne s'arrondissent pas, elles se refusent ; les bornes, elles, sont jugées par
 *    la règle de chaque saisie (`objectiveExceptionRefusal`), qui dit pourquoi.
 * ⚠️ VIDE N'EST PAS ZÉRO : un champ effacé ne demande aucune variation, il ne demande rien.
 */
export function parseSignedPercentBp(input: string): number | null {
  const s = input.trim().replace(/\s*%$/, '').replace('−', '-');
  const m = /^([+-]?)(\d{1,4})(?:[.,](\d{1,2}))?$/.exec(s);
  if (!m) return null;
  const bp = Number(m[2]) * 100 + Number((m[3] ?? '').padEnd(2, '0'));
  return m[1] === '-' ? -bp : bp;
}
